import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star } from 'lucide-react';
import { PARTNER_FORMULAS } from '../data/partnerFormulas';
import { useZomerkickAvailability } from '../lib/usePublicData';
import Sticker from './Sticker';

const FormulaCard = ({ formula, availability }) => {
    const accentBg = {
        cream: 'bg-cream',
        mint: 'bg-[var(--mint-soft)]',
        pink: 'bg-[var(--pink-soft)]',
        yellow: 'bg-[var(--yellow-soft)]',
    }[formula.accent] || 'bg-cream';

    const showCounter = formula.id === 'zomerkick' && availability;
    const counterLabel = !showCounter ? null
        : availability.available > 0
            ? `Nog ${availability.available} van ${availability.max} plekken vrij`
            : 'Volzet — nieuwe lijst start binnenkort';

    return (
        <article data-testid={`formula-card-${formula.id}`}
            className={`relative ${accentBg} ${formula.premium ? 'zz-card-premium mt-4' : 'zz-card-soft'} px-5 py-6 sm:px-6 sm:py-8 flex flex-col h-full min-w-0`}>
            {formula.premium && (
                <span data-testid={`formula-card-${formula.id}-badge`}
                    className="absolute -top-4 sm:-top-5 left-1/2 -translate-x-1/2 z-20 bg-[var(--green-dark)] text-cream font-display text-xs sm:text-sm uppercase tracking-wider px-5 py-2 rounded-full border-2 border-cream flex items-center gap-1.5 whitespace-nowrap shadow-[0_3px_0_var(--green-dark)] drop-shadow-[0_2px_4px_rgba(0,0,0,0.18)]">
                    <Star size={13} fill="currentColor" /> {formula.meta}
                </span>
            )}

            {showCounter && (
                <div className="flex justify-center mt-1 mb-2"
                    data-testid={`formula-card-${formula.id}-counter`}>
                    <Sticker variant={availability.available > 0 ? 'cream' : 'pink'} tilt="r"
                        className="!text-[10px] !py-1 !px-3">
                        {counterLabel}
                    </Sticker>
                </div>
            )}

            <h3 className="font-display zz-outline-title-dark"
                style={{
                    fontSize: 'clamp(1rem, 1.75vw, 1.5rem)',
                    letterSpacing: '-0.04em',
                    whiteSpace: 'nowrap',
                }}>
                {formula.name}
            </h3>
            <p className="mt-4 text-sm sm:text-base text-[#006b44]/90 leading-relaxed">
                {formula.intro}
            </p>
            <ul className="mt-6 space-y-2.5 flex-1">
                {formula.benefits.map((b, i) => (
                    <li key={i} data-testid={`formula-card-${formula.id}-benefit-${i}`}
                        className="flex items-start gap-2 text-sm text-[var(--green-dark)]">
                        <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[var(--green)] flex items-center justify-center">
                            <Check size={12} className="text-cream" />
                        </span>
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <Link to={`/#brochure?formule=${formula.id}`} data-testid={`formula-card-${formula.id}-cta`}
                className={`mt-7 zz-btn ${formula.premium ? 'zz-btn--primary' : 'zz-btn--cream'} zz-pulse-cta w-full !text-xs`}>
                Vraag partnerbrochure aan <ArrowRight size={16} />
            </Link>
        </article>
    );
};

export const BecomePartner = () => {
    const { data: availability } = useZomerkickAvailability();

    return (
        <section id="word-partner" data-testid="become-partner-section" className="relative py-16 sm:py-24 bg-vakjes">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="text-center mb-10 sm:mb-14">
                    <h2 data-testid="become-partner-title" className="zz-outline-title font-display"
                        style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}>
                        WORD PARTNER
                    </h2>
                    <p className="mt-4 text-sm sm:text-base text-[var(--green-dark)] max-w-2xl mx-auto bg-cream/90 inline-block px-5 py-2 rounded-full border-2 border-[var(--green)]">
                        Vier formules op maat van jouw onderneming.
                    </p>
                </div>

                <div className="bg-cream/95 backdrop-blur-sm border-2 border-[var(--green)] rounded-[32px] p-6 sm:p-10 shadow-[0_6px_0_var(--green-dark)]">
                    <p className="text-base sm:text-lg leading-relaxed text-[var(--green-dark)] max-w-3xl mx-auto text-center mb-10 sm:mb-12">
                        Door partner te worden van Zotte Zomer kies je voor zichtbaarheid, beleving en verbondenheid met
                        Ronse en haar inwoners. Vier formules — ZOMERKICK, ZOMERBOOST, ZOMERVIBES en ZOMERGOESTING — laten
                        jou kiezen hoe groot je rol wil zijn.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 sm:gap-7">
                        {PARTNER_FORMULAS.map((f) => (
                            <FormulaCard key={f.id} formula={f} availability={availability} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BecomePartner;
